import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { EventType } from 'src/app/models/eventType.model';
import { Event } from 'src/app/models/event.model';
import { Observable } from 'rxjs';
import { EventAttendee } from 'src/app/models/eventAttendee.model';
import { Group } from 'src/app/models/group.model';
import * as moment from 'moment';
import { AppSettingsService } from '../app-settings/app-settings.service';

@Injectable({
  providedIn: 'root'
})
export class EventService {

  constructor(
    private http: HttpClient,
    private settings: AppSettingsService,
  ) { }

  getEventsByUser(): Observable<Event[]> {
    const url = this.settings.apiUrl + `events`

    return this.http.get(url).pipe(
      map((data: Event[]) => {
        let events: Event[] = [];
        data.forEach(event => {
          events.push(new Event(event));
        });
        return events;
      })
    );
  }

  getMonthlyEventsByUser(date: string): Observable<Event[]> {
    const params = {
      month_date: date,
    }

    const url = this.settings.apiUrl + `events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: Event[]) => {
        let events: Event[] = [];
        data.forEach(event => {
          events.push(new Event(event));
        });
        return events;
      })
    );
  }

  getMonthlyEventsByUserAndPortal(date: string, portalId: number): Observable<Event[]> {
    const params = {
      month_date: date,
      portal_id: portalId
    }

    const url = this.settings.apiUrl + `events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: Event[]) => {
        let events: Event[] = [];
        data.forEach(event => {
          events.push(new Event(event));
        });
        return events;
      })
    );
  }

  getMonthlyEditableEventsByUserAndPortal(date: string, portalId: number): Observable<Event[]> {
    const params = {
      month_date: date,
      portal_id: portalId,
      use_can_edit: true
    }

    const url = this.settings.apiUrl + `events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: Event[]) => {
        let events: Event[] = [];
        if (data) {
          data.forEach(event => {
            events.push(new Event(event));
          });
        }
        
        return events;
      })
    );
  }

  getEventTypes() {
    const url = this.settings.apiUrl + `eventTypes`;

    return this.http.get(url).pipe(
      map((data: EventType[]) => {
        let eventTypes: EventType[] = [];
        data.forEach(eventType => {
          eventTypes.push(new EventType(eventType));
        });
        return eventTypes;
      })
    );
  }

  createEvent(event: Event, startDate: string, endDate: string, eventTypeID: number, reminderTime: string, portalId: number, groups: Group[]) {
    // Get the group ids from the selected groups
    let groupIds = [];
    for(let i = 0; i< groups.length; i++) {
      if (groups[i].groupSelected) {
        groupIds.push(groups[i].id);
      }
    }

    const body = {
      event_name: event.eventName,
      event_date: startDate,
      event_url: event.eventImageURL,
      event_description: event.eventDescription,
      event_type_id: eventTypeID,
      event_end_date: endDate,
      location: event.location,
      reminder_time: reminderTime,
      event_all_day: event.allDay,
      is_personal: event.isPersonal,
      portal_id: portalId,
      group_ids: groupIds
    }

    const url = this.settings.apiUrl + `events`;

    return this.http.post(url, {events: [body]}).pipe(
      map((data: Event[]) => {
        let newEvents: Event[] = [];
        if (data) {
          data.forEach(event => {
            newEvents.push(new Event(event));
          });
        }

        return newEvents;
      })
    );
  }

  createEvents(events: Event[], portalId: number) : Observable<Event[]> {

    let addedEvents = [];

    events.forEach(event => {
      let startDate = null;
      let endDate = null;
      if (event.allDay) {
        startDate = moment.utc(event.startDate).format('YYYY-MM-DD');
        endDate = moment.utc(event.endDate).format('YYYY-MM-DD');
      } else {
        startDate = moment.utc(event.startDate).format('YYYY-MM-DD HH:mm');
        endDate = moment.utc(event.endDate).format('YYYY-MM-DD HH:mm');
      }

      if (event.selectedReminder > 0) {
        event.reminderTime = moment.utc(event.startDate).subtract(event.selectedReminder, 'minutes').format('YYYY-MM-DD HH:mm');
      }

      let newEvent = {
        event_name: event.eventName,
        event_date: startDate,
        event_url: event.eventImageURL,
        event_description: event.eventDescription,
        event_type_id: event.eventTypeID,
        event_end_date: endDate,
        location: event.location,
        reminder_time: event.reminderTime,
        event_all_day: event.allDay,
        is_personal: event.isPersonal,
        portal_id: portalId,
        group_ids: event.selectedGroups
      }

      addedEvents.push(newEvent);
    })
    
    const body = {
      events: addedEvents
    }

    const url = this.settings.apiUrl + `events`;

    return this.http.post(url, body).pipe(
      map((data: Event[]) => {
        let newEvents: Event[] = [];
        if (data) {
          data.forEach(event => {
            newEvents.push(new Event(event));
          });
        }

        return newEvents;
      })
    );
  }

  updateEvents(events: Event[], portalId: number) : Observable<Event[]> {
    let addedEvents = [];

    events.forEach(event => {
      let startDate = null;
      let endDate = null;
      if (event.allDay) {
        startDate = moment.utc(event.startDate).format('YYYY-MM-DD');
        endDate = moment.utc(event.endDate).format('YYYY-MM-DD');
      } else {
        startDate = moment.utc(event.startDate).format('YYYY-MM-DD HH:mm');
        endDate = moment.utc(event.endDate).format('YYYY-MM-DD HH:mm');
      }

      if (event.selectedReminder > 0) {
        event.reminderTime = moment.utc(event.startDate).subtract(event.selectedReminder, 'minutes').format('YYYY-MM-DD HH:mm');
      }

      let newEvent = {
        id: event.id,
        event_name: event.eventName,
        event_date: startDate,
        event_url: event.eventImageURL,
        event_description: event.eventDescription,
        event_type_id: event.eventTypeID,
        event_end_date: endDate,
        location: event.location,
        reminder_time: event.reminderTime,
        event_all_day: event.allDay,
        is_personal: event.isPersonal,
        is_deleted: event.isDeleted,
        portal_id: portalId,
        group_ids: event.selectedGroups
      }

      addedEvents.push(newEvent);
    })
    
    const body = {
      events: addedEvents
    }

    const url = this.settings.apiUrl + `events`;

    return this.http.put(url, body).pipe(
      map((data: Event[]) => {
        let newEvents: Event[] = [];
        if (data) {
          data.forEach(event => {
            newEvents.push(new Event(event));
          });
        }

        return newEvents;
      })
    );
  }

  getEventByID(eventID: number): Observable<Event> {
    const url = this.settings.apiUrl + `events/${eventID}`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        return new Event(data);
      })
    );
  }

  getEventAttendees(eventID: number): Observable<EventAttendee[]> {
    const url = this.settings.apiUrl + `events/${eventID}/attendees`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let attendees: EventAttendee[] = [];
        data.forEach(attendee => {
          attendees.push(new EventAttendee(attendee));
        });
        return attendees;
      })
    );
  }

  getEventInvite(eventID: number): Observable<EventAttendee> {
    const params = {
      use_app_user_id: true
    }

    const url = this.settings.apiUrl + `events/${eventID}/attendees?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any[]) => {
        return new EventAttendee(data[0]);
      })
    );
  }

  getAttendingStatuses(): Observable<any[]> {
    const url = this.settings.apiUrl + `attendingStatus`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        return data;
      })
    );
  }

  addEventAttendees(eventID: number, attendeeIDs: number[]): Observable<EventAttendee[]> {
    const body = {
      attendee_ids: attendeeIDs
    }

    const url = this.settings.apiUrl + `events/${eventID}/attendees`;

    return this.http.post(url, body).pipe(
      map((data: any[]) => {
        let attendees: EventAttendee[] = [];
        data.forEach(attendee => {
          attendees.push(new EventAttendee(attendee));
        });
        return attendees;
      })
    );
  }

  updateEventAttendeeStatus(eventID: number, attendingStatusID: number) {
    const body = {
      attending_status_id: attendingStatusID
    }

    const url = this.settings.apiUrl + `events/${eventID}/attendees`;

    return this.http.put(url, body).pipe();
  }

  updateEvent(event: Event, startDate: string, endDate: string, eventTypeID: number, reminderTime: string, portalId: number, groups: Group[]) {
    let groupIds = [];
    for(let i = 0; i< groups.length; i++) {
      if (groups[i].groupSelected) {
        groupIds.push(groups[i].id);
      }
    }

    const body = {
      id: event.id,
      event_name: event.eventName,
      event_date: startDate,
      event_url: event.eventImageURL,
      event_description: event.eventDescription,
      event_type_id: eventTypeID,
      event_end_date: endDate,
      reminder_time: reminderTime,
      all_day: event.allDay,
      location: event.location,
      is_personal: event.isPersonal,
      portal_id: portalId,
      group_ids: groupIds
    }

    const url = this.settings.apiUrl + `events/${event.id}`;

    return this.http.put(url, body).pipe(
      map((data: Event) => {
        return new Event(data);
      })
    );
  }

  searchEvents(query: string): Observable<Event[]>  {
    const params = {
      query: query
    }

    const url = this.settings.apiUrl + `search/events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let events: Event[] = [];
        data.forEach(event => {
          events.push(new Event(event))
        });
        return events;
      })
    );
  }

  deleteEvent(eventID: number): Observable<any> {
    const url = this.settings.apiUrl + `events/${eventID}`;

    return this.http.delete(url).pipe(
      map((data: any[]) => {
        return data;
      })
    );
  }

  getCsvExport(portalId: number, startDate: string, endDate: string) : Observable<string> {
    const params = {
      portal_id: portalId,
      start_date: startDate,
      end_date: endDate
    }

    const url = this.settings.apiUrl + `csv/events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        const exportedEvents: string = data.exported_events;
        return exportedEvents;
      })
    );
  }

  getIcsExport(portalId: number, startDate: string, endDate: string) : Observable<string> {
    const params = {
      portal_id: portalId,
      start_date: startDate,
      end_date: endDate
    }

    const url = this.settings.apiUrl + `ics/events?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        const exportedEvents: string = data.exported_events;
        return exportedEvents;
      })
    );
  }
}
